import { Skill, Manifest, FilterOptions, McpServer } from '../types';
import { amplifyService } from '../AppBootstrap';

let cachedApiBase: string | null = null;

async function getApiBase(): Promise<string> {
  if (cachedApiBase) return cachedApiBase;
  const base = amplifyService.getApiInvokeUrl().replace(/\/$/, '');
  cachedApiBase = base;
  return base;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await amplifyService.getIdToken();
  return token ? { Authorization: token } : {};
}

export async function fetchSkillRegistry(): Promise<Manifest> {
  const base = await getApiBase();
  const res = await fetch(`${base}/skill-registry`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch skill registry: ${res.status}`);
  return res.json();
}

export async function fetchSkillContent(skillMdPath: string): Promise<string> {
  const base = await getApiBase();
  const res = await fetch(`${base}/skills/${encodeURIComponent(skillMdPath)}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch skill content: ${res.status}`);
  return res.text();
}

export interface SkillSubmission {
  name: string;
  display_name: string;
  author: string;
  description: string;
  tags: string[];
  body: string;
}

export async function submitSkill(payload: SkillSubmission): Promise<string> {
  const base = await getApiBase();
  const res = await fetch(`${base}/skills`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  const text = await res.text().catch(() => '');
  if (!res.ok) throw new Error(text || `Request failed: ${res.status}`);
  const data = JSON.parse(text);
  return data.branch as string;
}

export type SubmissionStatus = 'running' | 'passed' | 'failed';

export async function getSubmissionStatus(branch: string): Promise<SubmissionStatus> {
  const base = await getApiBase();
  const res = await fetch(`${base}/submissions/${encodeURIComponent(branch)}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Status check failed: ${res.status}`);
  const data = await res.json();
  return data.status as SubmissionStatus;
}

export async function fetchMcpRegistry(): Promise<McpServer[]> {
  const base = await getApiBase();
  const res = await fetch(`${base}/register-mcp`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch MCP registry: ${res.status}`);
  return res.json();
}

export function getAllMcpEnvironments(servers: McpServer[]): string[] {
  const envSet = new Set<string>();
  servers.forEach((s) => { if (s.environment) envSet.add(s.environment); });
  return Array.from(envSet).sort();
}

export function getAllOwners(skills: Skill[], servers: McpServer[]): string[] {
  const ownerSet = new Set<string>();
  skills.forEach((s) => { if (s.owner) ownerSet.add(s.owner); });
  servers.forEach((s) => { if (s.owner) ownerSet.add(s.owner); });
  return Array.from(ownerSet).sort();
}

export function filterMcpServers(servers: McpServer[], search: string, sort: 'recent' | 'alpha' | 'popular' = 'recent', tags: string[] = [], environments: string[] = [], owners: string[] = [], starCounts?: Record<string, number>): McpServer[] {
  let results = [...servers];
  if (search.trim()) {
    const query = search.toLowerCase().trim();
    results = results.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        s.description?.toLowerCase().includes(query) ||
        s.owner.toLowerCase().includes(query) ||
        s.tools.some((t) => t.toLowerCase().includes(query))
    );
  }
  if (tags.length > 0) {
    results = results.filter((s) => tags.some((tag) => s.tags?.includes(tag)));
  }
  if (environments.length > 0) {
    results = results.filter((s) => s.environment && environments.includes(s.environment));
  }
  if (owners.length > 0) {
    results = results.filter((s) => owners.includes(s.owner));
  }
  if (sort === 'recent') {
    results.sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime());
  } else if (sort === 'popular') {
    const counts = starCounts ?? {};
    results.sort((a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0) || a.name.localeCompare(b.name));
  } else {
    results.sort((a, b) => a.name.localeCompare(b.name));
  }
  return results;
}

export function getAllTags(skills: Skill[]): string[] {
  const tagSet = new Set<string>();
  skills.forEach((skill) => {
    skill.tags.forEach((tag) => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
}

export function getAllMcpTags(servers: McpServer[]): string[] {
  const tagSet = new Set<string>();
  servers.forEach((s) => s.tags?.forEach((t) => tagSet.add(t)));
  return Array.from(tagSet).sort();
}

// Only considers a skill "recently updated" if it was modified AFTER its initial creation
export function isRecentlyUpdated(skill: Skill, daysThreshold = 7): boolean {
  const created = new Date(skill.created_at).getTime();
  const updated = new Date(skill.last_updated).getTime();
  if (updated <= created) return false;
  const diffMs = Date.now() - updated;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays <= daysThreshold;
}

export function filterSkills(
  skills: Skill[],
  { search = '', tags = [], owners = [], sort = 'recent', starCounts }: FilterOptions
): Skill[] {
  let results = [...skills];

  if (search.trim()) {
    const query = search.toLowerCase().trim();
    results = results.filter(
      (skill) =>
        skill.name.toLowerCase().includes(query) ||
        skill.description.toLowerCase().includes(query) ||
        skill.owner.toLowerCase().includes(query) ||
        skill.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  }

  if (tags.length > 0) {
    results = results.filter((skill) =>
      tags.some((tag) => skill.tags.includes(tag))
    );
  }

  if (owners.length > 0) {
    results = results.filter((skill) => owners.includes(skill.owner));
  }

  if (sort === 'recent') {
    results.sort(
      (a, b) =>
        new Date(b.last_updated).getTime() -
        new Date(a.last_updated).getTime()
    );
  } else if (sort === 'popular') {
    const counts = starCounts ?? {};
    results.sort((a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0) || a.name.localeCompare(b.name));
  } else if (sort === 'alpha') {
    results.sort((a, b) => a.name.localeCompare(b.name));
  }

  return results;
}

// --- Stars API ---

export interface StarInfo {
  starred: boolean;
  starCount: number;
}

export interface BulkStarData {
  counts: Record<string, number>;
  userStars: string[];
}

export async function toggleStar(itemId: string, itemType: 'skill' | 'mcp-server'): Promise<StarInfo> {
  const base = await getApiBase();
  const res = await fetch(`${base}/stars/${encodeURIComponent(itemId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ itemType }),
  });
  if (!res.ok) throw new Error(`Failed to toggle star: ${res.status}`);
  return res.json();
}

export async function getStarInfo(itemId: string): Promise<StarInfo> {
  const base = await getApiBase();
  const res = await fetch(`${base}/stars/${encodeURIComponent(itemId)}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to get star info: ${res.status}`);
  return res.json();
}

export async function fetchAllStars(): Promise<BulkStarData> {
  const base = await getApiBase();
  const res = await fetch(`${base}/stars`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch stars: ${res.status}`);
  return res.json();
}

export function getRelatedSkills(currentTags: string[], currentId: string, skills: Skill[], limit = 4): Skill[] {
  return skills
    .filter((s) => s.id !== currentId && s.tags.some((t) => currentTags.includes(t)))
    .sort(
      (a, b) =>
        b.tags.filter((t) => currentTags.includes(t)).length -
        a.tags.filter((t) => currentTags.includes(t)).length
    )
    .slice(0, limit);
}

export function getRelatedMcpServers(currentTags: string[], currentId: string, servers: McpServer[], limit = 4): McpServer[] {
  return servers
    .filter((s) => s.id !== currentId && s.tags?.some((t) => currentTags.includes(t)))
    .sort(
      (a, b) =>
        (b.tags ?? []).filter((t) => currentTags.includes(t)).length -
        (a.tags ?? []).filter((t) => currentTags.includes(t)).length
    )
    .slice(0, limit);
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function timeAgo(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}
